/**
 * Generate Invoice Modal
 * Preview invoice data before generation
 */

import React, { useState } from 'react';
import { X, FileText, Users, DollarSign, AlertCircle, Loader } from 'lucide-react';
import monthlyInvoiceService, { PreviewInvoiceData } from '../../services/monthlyInvoiceService';
import type { Assureur } from '../../types';

interface GenerateInvoiceModalProps {
  assureurs: Assureur[];
  onClose: () => void;
  onGenerated: () => void;
}

const GenerateInvoiceModal: React.FC<GenerateInvoiceModalProps> = ({
  assureurs,
  onClose,
  onGenerated,
}) => {
  const [step, setStep] = useState<'select' | 'preview' | 'generating'>('select');
  const [formData, setFormData] = useState({
    assureur_id: '',
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    remise_percentage: '2.00',
  });
  const [previewData, setPreviewData] = useState<PreviewInvoiceData | null>(null);
  const [error, setError] = useState('');

  const handlePreview = async () => {
    if (!formData.assureur_id) {
      setError('Veuillez sélectionner une compagnie d\'assurance');
      return;
    }

    setStep('preview');
    setError('');

    try {
      const data = await monthlyInvoiceService.previewInvoice(
        parseInt(formData.assureur_id),
        formData.month,
        formData.year
      );
      setPreviewData(data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erreur lors du chargement des données');
      setStep('select');
    }
  };

  const handleGenerate = async () => {
    setStep('generating');
    setError('');

    try {
      await monthlyInvoiceService.generateInvoice({
        assureur_id: parseInt(formData.assureur_id),
        month: formData.month,
        year: formData.year,
        remise_percentage: formData.remise_percentage,
      });
      onGenerated();
    } catch (err: any) {
      console.error('Error generating invoice:', err);
      console.error('Error response:', err.response);

      // Try to extract the most specific error message
      let errorMessage = 'Erreur lors de la génération de la facture';

      if (err.response?.data) {
        // Check for various error formats
        if (err.response.data.error) {
          errorMessage = err.response.data.error;
        } else if (err.response.data.non_field_errors) {
          errorMessage = Array.isArray(err.response.data.non_field_errors)
            ? err.response.data.non_field_errors[0]
            : err.response.data.non_field_errors;
        } else if (err.response.data.detail) {
          errorMessage = err.response.data.detail;
        } else if (typeof err.response.data === 'string') {
          errorMessage = err.response.data;
        } else {
          // Try to get the first error from any field
          const firstError = Object.values(err.response.data)[0];
          if (firstError) {
            errorMessage = Array.isArray(firstError) ? firstError[0] : String(firstError);
          }
        }
      }

      setError(errorMessage);
      setStep('preview');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-bold text-gray-900">Générer Facture Mensuelle</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            disabled={step === 'generating'}
            title="Fermer"
            aria-label="Fermer"
          >
            <X className="w-6 h-6" />
            <span className="sr-only">Fermer</span>
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {step === 'select' && (
            <>
              <div className="space-y-4">
                <div>
                  <label htmlFor="assureur" className="block text-sm font-medium text-gray-700 mb-2">
                    Compagnie d'assurance *
                  </label>
                  <select
                    id="assureur"
                    title="Compagnie d'assurance"
                    value={formData.assureur_id}
                    onChange={(e) => setFormData({ ...formData, assureur_id: e.target.value })}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-teal-500"
                    required
                  >
                    <option value="">Sélectionnez une compagnie</option>
                    {assureurs.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.nom}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="month" className="block text-sm font-medium text-gray-700 mb-2">Mois *</label>
                    <select
                      id="month"
                      title="Mois"
                      value={formData.month}
                      onChange={(e) => setFormData({ ...formData, month: parseInt(e.target.value) })}
                      className="w-full border border-gray-200 rounded-xl px-3 py-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-teal-500"
                    >
                      {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
                        <option key={month} value={month}>
                          {monthlyInvoiceService.getMonthName(month)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                      <label htmlFor="year" className="block text-sm font-medium text-gray-700 mb-2">Année *</label>
                      <select
                        id="year"
                        title="Année"
                        value={formData.year}
                        onChange={(e) => setFormData({ ...formData, year: parseInt(e.target.value) })}
                        className="w-full border border-gray-200 rounded-xl px-3 py-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-teal-500"
                      >
                        {[2023, 2024, 2025, 2026].map((year) => (
                          <option key={year} value={year}>
                            {year}
                          </option>
                        ))}
                      </select>
                    </div>
                </div>

                <div>
                  <label htmlFor="remise_percentage" className="block text-sm font-medium text-gray-700 mb-2">
                    Remise (%) *
                  </label>
                  <input
                    id="remise_percentage"
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    value={formData.remise_percentage}
                    onChange={(e) => setFormData({ ...formData, remise_percentage: e.target.value })}
                    placeholder="2.00"
                    title="Remise en pourcentage"
                    className="w-full border border-gray-200 rounded-xl px-3 py-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Remise standard: 2%</p>
                </div>
              </div>

              {error && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              <div className="mt-6 flex justify-end gap-3">
                <button
                  onClick={onClose}
                  className="px-4 py-2 border border-gray-200 rounded-xl text-gray-700 hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  onClick={handlePreview}
                  className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 flex items-center gap-2"
                >
                  <FileText className="w-4 h-4" />
                  Aperçu
                </button>
              </div>
            </>
          )}

          {step === 'preview' && previewData && (
            <>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <h3 className="font-semibold text-blue-900 mb-2">
                  {previewData.assureur} - {monthlyInvoiceService.getMonthName(previewData.month)}{' '}
                  {previewData.year}
                </h3>

                {previewData.invoice_count === 0 ? (
                  <div className="text-orange-700">{previewData.message}</div>
                ) : (
                  <div className="grid grid-cols-2 gap-4 mt-3">
                    <div className="flex items-center gap-2">
                      <Users className="w-5 h-5 text-blue-600" />
                      <div>
                        <div className="text-xs text-blue-700">Patients</div>
                        <div className="font-semibold text-blue-900">{previewData.patient_count}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <FileText className="w-5 h-5 text-blue-600" />
                      <div>
                        <div className="text-xs text-blue-700">Factures</div>
                        <div className="font-semibold text-blue-900">{previewData.invoice_count}</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {previewData.invoice_count > 0 && (
                <>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Montant total:</span>
                      <span className="font-semibold text-gray-900">
                        {monthlyInvoiceService.formatCurrency(previewData.total_amount)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Remise (2%):</span>
                      <span className="font-semibold text-red-600">
                        - {monthlyInvoiceService.formatCurrency(previewData.remise_amount)}
                      </span>
                    </div>
                    <div className="border-t border-gray-300 pt-3 flex justify-between items-center">
                      <span className="font-semibold text-gray-900">Net à payer:</span>
                      <span className="text-xl font-bold text-green-600">
                        {monthlyInvoiceService.formatCurrency(previewData.net_to_pay)}
                      </span>
                    </div>
                  </div>

                  {previewData.invoices && previewData.invoices.length > 0 && (
                    <div className="mt-4">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">
                        Aperçu des factures (premières 10):
                      </h4>
                      <div className="max-h-48 overflow-y-auto border rounded-lg">
                        <table className="min-w-full text-xs">
                          <thead className="bg-gray-50 sticky top-0">
                            <tr>
                              <th className="px-3 py-2 text-left text-gray-700 font-medium">N°</th>
                              <th className="px-3 py-2 text-left text-gray-700 font-medium">Patient</th>
                              <th className="px-3 py-2 text-left text-gray-700 font-medium">Date</th>
                              <th className="px-3 py-2 text-right text-gray-700 font-medium">Montant</th>
                            </tr>
                          </thead>
                          <tbody>
                            {previewData.invoices.map((inv, idx) => (
                              <tr key={idx} className="border-t">
                                <td className="px-3 py-2 text-gray-700">{inv.numero}</td>
                                <td className="px-3 py-2 text-gray-700">{inv.patient}</td>
                                <td className="px-3 py-2 text-gray-700">{inv.date_emission}</td>
                                <td className="px-3 py-2 text-right text-gray-700">
                                  {monthlyInvoiceService.formatCurrency(inv.montant_assurance)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </>
              )}

              {error && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              <div className="mt-6 flex justify-end gap-3">
                <button
                  onClick={() => setStep('select')}
                  className="px-4 py-2 border border-gray-200 rounded-xl text-gray-700 hover:bg-gray-50"
                >
                  Retour
                </button>
                {previewData.invoice_count > 0 && (
                  <button
                    onClick={handleGenerate}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
                  >
                    <DollarSign className="w-4 h-4" />
                    Générer la Facture
                  </button>
                )}
              </div>
            </>
          )}

          {step === 'generating' && (
            <div className="text-center py-8">
              <Loader className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
              <p className="text-gray-700 font-medium">Génération de la facture en cours...</p>
              <p className="text-sm text-gray-500 mt-2">Veuillez patienter</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GenerateInvoiceModal;
